import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { getDashboardStats, getTasks, getActivityLogs, getUsers } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ScrollArea } from '../components/ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  Zap, CheckCircle2, Clock, ListTodo, ArrowRight, 
  CalendarDays, Plus, Building, User, Users, Edit, FolderKanban
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  ResponsiveContainer, PieChart, Pie, Cell, Tooltip
} from 'recharts';

const COLORS = {
  'To Do': '#6B7280',
  'In Progress': '#3B82F6',
  'Completed': '#22C55E'
};

import { useDepartments } from '../hooks/useDepartments';

export const DashboardPage = () => {
  const DEPARTMENTS = useDepartments();
  const { user, isSuperAdmin } = useAuth();
  const [stats, setStats] = useState(null);
  const [recentTasks, setRecentTasks] = useState([]);
  const [activityLogs, setActivityLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('todo');
  const [selectedDept, setSelectedDept] = useState('');
  const [dashScope, setDashScope] = useState(() => {
    return sessionStorage.getItem('dashScope') || 'my';
  });
  const [teamUsers, setTeamUsers] = useState([]);

  useEffect(() => {
    // Load active users for the current dept (HR only — to derive direct reportees)
    if (user?.department === 'Human Resources Management') {
      (async () => {
        try {
          const res = await getUsers({ status: 'Active', department: user.department });
          setTeamUsers(res.data || []);
        } catch { /* noop */ }
      })();
      // HR users should never be on "department" scope; coerce to "my"
      if (dashScope === 'department') setDashScope('my');
    }
  }, [user?.department]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (isSuperAdmin && selectedDept) params.department = selectedDept;

      const taskParams = { limit: 100 };
      if (isSuperAdmin && selectedDept) taskParams.department = selectedDept;

      const [statsRes, tasksRes, logsRes] = await Promise.all([
        getDashboardStats(params),
        getTasks(taskParams),
        getActivityLogs({ limit: 10, user_id: user?.id })
      ]);
      setStats(statsRes.data);
      setRecentTasks(tasksRes.data);
      setActivityLogs(logsRes.data);
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedDept, isSuperAdmin, user?.id]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const getPriorityLabel = (priority, dept) => {
    const isDD = dept === 'Design & Development';
    if (isDD) {
      if (priority === 1) return { label: 'High', color: 'text-red-500 bg-red-500/10' };
      if (priority <= 3) return { label: 'Medium', color: 'text-yellow-500 bg-yellow-500/10' };
      return { label: 'Low', color: 'text-green-500 bg-green-500/10' };
    }
    if (priority <= 3) return { label: 'High', color: 'text-red-500 bg-red-500/10' };
    if (priority <= 6) return { label: 'Medium', color: 'text-yellow-500 bg-yellow-500/10' };
    return { label: 'Low', color: 'text-green-500 bg-green-500/10' };
  };

  const weeklyStatusData = stats?.weekly_status ? [
    { name: 'To Do', value: stats.weekly_status['To Do'] || 0, fill: COLORS['To Do'] },
    { name: 'In Progress', value: stats.weekly_status['In Progress'] || 0, fill: COLORS['In Progress'] },
    { name: 'Completed', value: stats.weekly_status['Completed'] || 0, fill: COLORS['Completed'] },
    { name: 'Cancelled', value: stats.weekly_status['Cancelled'] || 0, fill: '#6B7280' }
  ].filter(item => item.value > 0) : [];

  // Scope-filtered tasks for the dashboard task list
  const isHRUser = user?.department === 'Human Resources Management';
  // Recursive reportee IDs (HR hierarchy): BFS via manager_email (and manager_email_2)
  const directReporteeIds = (() => {
    if (!isHRUser || !user?.email) return [];
    const byManager = new Map();
    teamUsers.forEach(m => {
      [m.manager_email, m.manager_email_2].forEach(mgrEmail => {
        const mgr = (mgrEmail || '').toLowerCase();
        if (!mgr) return;
        if (!byManager.has(mgr)) byManager.set(mgr, []);
        byManager.get(mgr).push(m);
      });
    });
    const queue = [user.email.toLowerCase()];
    const collected = new Set();
    while (queue.length) {
      const mgrEmail = queue.shift();
      const directs = byManager.get(mgrEmail) || [];
      for (const m of directs) {
        if (collected.has(m.id)) continue;
        collected.add(m.id);
        if (m.email) queue.push(m.email.toLowerCase());
      }
    }
    return Array.from(collected);
  })();
  const hasReportees = directReporteeIds.length > 0;
  // HR: toggle is "my" vs "team" (direct reportees) — only if user has reportees
  // Non-HR: toggle is "my" vs "department"
  // Super Admin: no toggle at all
  const showDashToggle = !isSuperAdmin && (isHRUser ? hasReportees : true);

  let scopedRecentTasks = recentTasks;
  // My Tasks includes team tasks where the user is any assignee (primary OR team member)
  const isMine = (t) => t.assigned_to === user?.id || (t.assigned_to_list || []).includes(user?.id);
  if (showDashToggle) {
    if (isHRUser) {
      if (dashScope === 'my') {
        scopedRecentTasks = recentTasks.filter(isMine);
      } else if (dashScope === 'team') {
        scopedRecentTasks = recentTasks.filter(t =>
          directReporteeIds.includes(t.assigned_to) ||
          (t.assigned_to_list || []).some(id => directReporteeIds.includes(id))
        );
      }
    } else if (dashScope === 'my') {
      scopedRecentTasks = recentTasks.filter(isMine);
    }
  } else if (isHRUser && !hasReportees) {
    // HR Member with no reportees → My Tasks only
    scopedRecentTasks = recentTasks.filter(isMine);
  }

  // D&D-specific UI: show In Review tab only when user is in D&D (or Super Admin)
  const isDDContext = isSuperAdmin || user?.department === 'Design & Development';
  // HR-specific UI: show On Hold tab only when user is in HR (or Super Admin)
  const isHRContext = isSuperAdmin || user?.department === 'Human Resources Management';

  const scopedTodo = scopedRecentTasks.filter(t => t.status === 'To Do').sort((a, b) => a.priority - b.priority);
  const scopedInProgress = scopedRecentTasks.filter(t => t.status === 'In Progress').sort((a, b) => a.priority - b.priority);
  const scopedInReview = scopedRecentTasks.filter(t => t.status === 'In Review').sort((a, b) => a.priority - b.priority);
  const scopedOnHold = scopedRecentTasks.filter(t => t.status === 'On Hold').sort((a, b) => a.priority - b.priority);
  const scopedCompleted = scopedRecentTasks.filter(t => t.status === 'Completed');
  const scopedCancelled = scopedRecentTasks.filter(t => t.status === 'Cancelled');
  const myTaskCount = recentTasks.filter(isMine).length;

  React.useEffect(() => {
    sessionStorage.setItem('dashScope', dashScope);
  }, [dashScope]);

  // Role-based label for KPIs
  const scopeLabel = stats?.scope_label || (isSuperAdmin ? 'All Departments' : user?.role === 'Admin' ? user?.department : 'Personal');

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#F5A623]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-heading font-bold">
            Welcome back, {user?.full_name?.split(' ')[0]}!
          </h1>
          <p className="text-muted-foreground mt-1 text-sm">
            {scopeLabel} overview
          </p>
        </div>
        <div className="flex items-center gap-3">
          {/* Super Admin department filter */}
          {isSuperAdmin && (
            <Select value={selectedDept || "all"} onValueChange={(v) => setSelectedDept(v === "all" ? "" : v)}>
              <SelectTrigger className="w-52" data-testid="dept-filter">
                <Building className="h-4 w-4 mr-2" />
                <SelectValue placeholder="All Departments" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Departments</SelectItem>
                {DEPARTMENTS.map(d => (<SelectItem key={d} value={d}>{d}</SelectItem>))}
              </SelectContent>
            </Select>
          )}
          <Button asChild className="bg-[#F5A623] hover:bg-[#D48C1B] text-black font-semibold">
            <Link to="/tasks/new" data-testid="create-task-btn">
              <Plus className="h-4 w-4 mr-1.5" /> Create Task
            </Link>
          </Button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className={`grid grid-cols-2 ${(isDDContext || isHRContext) ? 'lg:grid-cols-5' : 'lg:grid-cols-4'} gap-4`}>
        <Card className="border-border/50 hover:border-[#F5A623]/50 transition-colors" data-testid="stat-total-tasks">
          <CardContent className="p-4 sm:p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs sm:text-sm text-muted-foreground">Total Tasks</p>
                <p className="text-2xl sm:text-3xl font-bold mt-1">{stats?.total_tasks || 0}</p>
              </div>
              <div className="h-10 w-10 sm:h-12 sm:w-12 rounded-lg bg-[#F5A623]/10 flex items-center justify-center">
                <Zap className="h-5 w-5 sm:h-6 sm:w-6 text-[#F5A623]" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-border/50 hover:border-gray-500/50 transition-colors" data-testid="stat-todo">
          <CardContent className="p-4 sm:p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs sm:text-sm text-muted-foreground">To Do</p>
                <p className="text-2xl sm:text-3xl font-bold mt-1 text-gray-600 dark:text-gray-400">{stats?.todo_tasks || 0}</p>
              </div>
              <div className="h-10 w-10 sm:h-12 sm:w-12 rounded-lg bg-gray-500/10 flex items-center justify-center">
                <ListTodo className="h-5 w-5 sm:h-6 sm:w-6 text-gray-500" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-border/50 hover:border-[#3B82F6]/50 transition-colors" data-testid="stat-in-progress">
          <CardContent className="p-4 sm:p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs sm:text-sm text-muted-foreground">In Progress</p>
                <p className="text-2xl sm:text-3xl font-bold mt-1 text-[#3B82F6]">{stats?.in_progress_tasks || 0}</p>
              </div>
              <div className="h-10 w-10 sm:h-12 sm:w-12 rounded-lg bg-[#3B82F6]/10 flex items-center justify-center">
                <Clock className="h-5 w-5 sm:h-6 sm:w-6 text-[#3B82F6]" />
              </div>
            </div>
          </CardContent>
        </Card>

        {isDDContext && (
          <Card className="border-border/50 hover:border-amber-500/50 transition-colors" data-testid="stat-in-review">
            <CardContent className="p-4 sm:p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs sm:text-sm text-muted-foreground">In Review</p>
                  <p className="text-2xl sm:text-3xl font-bold mt-1 text-amber-500">{stats?.in_review_tasks || 0}</p>
                </div>
                <div className="h-10 w-10 sm:h-12 sm:w-12 rounded-lg bg-amber-500/10 flex items-center justify-center">
                  <Clock className="h-5 w-5 sm:h-6 sm:w-6 text-amber-500" />
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {isHRContext && (
          <Card className="border-border/50 hover:border-purple-500/50 transition-colors" data-testid="stat-on-hold">
            <CardContent className="p-4 sm:p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs sm:text-sm text-muted-foreground">On Hold</p>
                  <p className="text-2xl sm:text-3xl font-bold mt-1 text-purple-500">{stats?.on_hold_tasks || 0}</p>
                </div>
                <div className="h-10 w-10 sm:h-12 sm:w-12 rounded-lg bg-purple-500/10 flex items-center justify-center">
                  <Clock className="h-5 w-5 sm:h-6 sm:w-6 text-purple-500" />
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        <Card className="border-border/50 hover:border-[#22C55E]/50 transition-colors" data-testid="stat-completed">
          <CardContent className="p-4 sm:p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs sm:text-sm text-muted-foreground">Completed</p>
                <p className="text-2xl sm:text-3xl font-bold mt-1 text-[#22C55E]">{stats?.completed_tasks || 0}</p>
              </div>
              <div className="h-10 w-10 sm:h-12 sm:w-12 rounded-lg bg-[#22C55E]/10 flex items-center justify-center">
                <CheckCircle2 className="h-5 w-5 sm:h-6 sm:w-6 text-[#22C55E]" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Tasks Tabs */}
        <div className="lg:col-span-2">
          <Card className="border-border/50">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg font-heading flex items-center justify-between">
                <span data-testid="dash-tasks-title">{isSuperAdmin ? 'All Tasks' : isHRUser ? (dashScope === 'team' ? "My Team's Tasks" : 'My Tasks') : (dashScope === 'my' ? 'My Tasks' : 'Department Tasks')}</span>
                <Button variant="ghost" size="sm" asChild className="text-[#F5A623]">
                  <Link to="/tasks">View All <ArrowRight className="ml-1 h-4 w-4" /></Link>
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {/* Scope Toggle (not for Super Admin) */}
              {showDashToggle && (
                <div className="flex items-center gap-1 bg-muted/50 rounded-lg p-1 mb-4" data-testid="dash-scope-toggle">
                  <button
                    onClick={() => setDashScope('my')}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
                      dashScope === 'my'
                        ? 'bg-background text-foreground shadow-sm'
                        : 'text-muted-foreground hover:text-foreground'
                    }`}
                    data-testid="dash-scope-my"
                  >
                    <User className="h-3.5 w-3.5" />
                    My Tasks
                    <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${dashScope === 'my' ? 'bg-[#F5A623]/15 text-[#F5A623]' : 'bg-muted text-muted-foreground'}`}>
                      {myTaskCount}
                    </span>
                  </button>
                  {isHRUser ? (
                    <button
                      onClick={() => setDashScope('team')}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
                        dashScope === 'team'
                          ? 'bg-background text-foreground shadow-sm'
                          : 'text-muted-foreground hover:text-foreground'
                      }`}
                      data-testid="dash-scope-team"
                    >
                      <Users className="h-3.5 w-3.5" />
                      My Team's Tasks
                      <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${dashScope === 'team' ? 'bg-[#F5A623]/15 text-[#F5A623]' : 'bg-muted text-muted-foreground'}`}>
                        {recentTasks.filter(t => directReporteeIds.includes(t.assigned_to) || (t.assigned_to_list || []).some(id => directReporteeIds.includes(id))).length}
                      </span>
                    </button>
                  ) : (
                    <button
                      onClick={() => setDashScope('department')}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
                        dashScope === 'department'
                          ? 'bg-background text-foreground shadow-sm'
                          : 'text-muted-foreground hover:text-foreground'
                      }`}
                      data-testid="dash-scope-department"
                    >
                      <Users className="h-3.5 w-3.5" />
                      Department Tasks
                      <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${dashScope === 'department' ? 'bg-[#F5A623]/15 text-[#F5A623]' : 'bg-muted text-muted-foreground'}`}>
                        {recentTasks.length}
                      </span>
                    </button>
                  )}
                </div>
              )}

              <Tabs value={activeTab} onValueChange={setActiveTab}>
                <TabsList className={`grid ${(isDDContext || isHRContext) ? 'grid-cols-5' : 'grid-cols-4'} w-full mb-4`}>
                  <TabsTrigger value="todo" className="text-xs sm:text-sm" data-testid="dash-tab-todo">To Do ({scopedTodo.length})</TabsTrigger>
                  <TabsTrigger value="inprogress" className="text-xs sm:text-sm" data-testid="dash-tab-inprogress">In Progress ({scopedInProgress.length})</TabsTrigger>
                  {isDDContext && (
                    <TabsTrigger value="inreview" className="text-xs sm:text-sm" data-testid="dash-tab-inreview">In Review ({scopedInReview.length})</TabsTrigger>
                  )}
                  {isHRContext && !isDDContext && (
                    <TabsTrigger value="onhold" className="text-xs sm:text-sm" data-testid="dash-tab-onhold">On Hold ({scopedOnHold.length})</TabsTrigger>
                  )}
                  <TabsTrigger value="completed" className="text-xs sm:text-sm" data-testid="dash-tab-completed">Completed ({scopedCompleted.length})</TabsTrigger>
                  <TabsTrigger value="cancelled" className="text-xs sm:text-sm" data-testid="dash-tab-cancelled">Cancelled ({scopedCancelled.length})</TabsTrigger>
                </TabsList>
                
                {[
                  { key: 'todo', tasks: scopedTodo },
                  { key: 'inprogress', tasks: scopedInProgress },
                  ...(isDDContext ? [{ key: 'inreview', tasks: scopedInReview }] : []),
                  ...(isHRContext && !isDDContext ? [{ key: 'onhold', tasks: scopedOnHold }] : []),
                  { key: 'completed', tasks: scopedCompleted },
                  { key: 'cancelled', tasks: scopedCancelled },
                ].map(({ key, tasks }) => (
                  <TabsContent key={key} value={key} className="mt-0">
                    <ScrollArea className="h-[280px]">
                      {tasks.length === 0 ? (
                        <p className="text-center text-muted-foreground py-8">No tasks</p>
                      ) : (
                        <div className="space-y-2">
                          {tasks.slice(0, 8).map((task) => (
                            <TaskCard key={task.id} task={task} getPriorityLabel={getPriorityLabel} />
                          ))}
                        </div>
                      )}
                    </ScrollArea>
                  </TabsContent>
                ))}
              </Tabs>
            </CardContent>
          </Card>
        </div>

        {/* Right Sidebar */}
        <div className="space-y-6">
          {/* Weekly Status */}
          <Card className="border-border/50" data-testid="weekly-status">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg font-heading flex items-center gap-2">
                <CalendarDays className="h-5 w-5" />
                Weekly Status
              </CardTitle>
            </CardHeader>
            <CardContent>
              {weeklyStatusData.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">No data this week</p>
              ) : (
                <>
                  <div className="h-40">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie data={weeklyStatusData} cx="50%" cy="50%" innerRadius={40} outerRadius={60} paddingAngle={3} dataKey="value">
                          {weeklyStatusData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.fill} />
                          ))}
                        </Pie>
                        <Tooltip />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="flex justify-center gap-4 mt-2">
                    {weeklyStatusData.map((item) => (
                      <div key={item.name} className="flex items-center gap-1.5">
                        <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: item.fill }} />
                        <span className="text-xs text-muted-foreground">{item.name}: {item.value}</span>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          {/* Recent Activity */}
          <Card className="border-border/50" data-testid="activity-log">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg font-heading">My Activity</CardTitle>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-48">
                {activityLogs.length === 0 ? (
                  <p className="text-center text-muted-foreground py-4">No recent activity</p>
                ) : (
                  <div className="space-y-3">
                    {activityLogs.slice(0, 6).map((log) => (
                      <div key={log.id} className="flex gap-2">
                        <div className="h-1.5 w-1.5 rounded-full bg-[#F5A623] mt-2 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="text-xs line-clamp-2">{log.details}</p>
                          <p className="text-[10px] text-muted-foreground mt-0.5">{new Date(log.timestamp).toLocaleString()}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </ScrollArea>
            </CardContent>
          </Card>
        </div>
      </div>

    </div>
  );
};

const TaskCard = ({ task, getPriorityLabel }) => {
  const navigate = useNavigate();
  const priority = getPriorityLabel(task.priority, task.department);
  const isOverdue = (() => {
    if (task.status === 'Completed' || task.status === 'Cancelled') return false;
    const due = new Date(task.due_date);
    due.setHours(23, 59, 59, 999);
    return new Date() > due;
  })();
  
  return (
    <Link to={`/tasks/${task.id}`} className="block p-3 rounded-lg border border-border hover:border-[#F5A623]/50 hover:bg-accent/50 transition-all group" data-testid={`task-card-${task.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <p className="font-medium text-sm truncate">{task.task_name}</p>
          <div className="flex items-center gap-2 mt-1.5 flex-wrap">
            {task.work_type === 'Team' && task.department === 'Design & Development' && <span className="text-[9px] px-1 py-0 rounded bg-blue-500/15 text-blue-600 font-semibold border border-blue-500/20"><Users className="h-2.5 w-2.5 mr-0.5 inline" />Team</span>}
            {task.project_name && <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#F5A623]/15 text-[#F5A623] font-medium" data-testid={`dash-task-project-${task.id}`}><FolderKanban className="h-2.5 w-2.5 mr-0.5 inline" />{task.project_name}</span>}
            {task.assigned_to_name && <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-500/10 text-blue-600 font-medium">{task.assigned_to_name}</span>}
            <Badge variant="outline" className="text-[10px] px-1.5 py-0">{task.category}</Badge>
            <span className={`text-[10px] px-1.5 py-0.5 rounded ${priority.color}`}>P{task.priority}</span>
            {isOverdue && <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-500/10 text-red-500">Overdue</span>}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <p className={`text-[10px] ${isOverdue ? 'text-red-500' : 'text-muted-foreground'}`}>{new Date(task.due_date).toLocaleDateString()}</p>
          <button
            type="button"
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); navigate(`/tasks/${task.id}/edit`); }}
            className="opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded hover:bg-[#F5A623]/15"
            data-testid={`task-edit-${task.id}`}
          >
            <Edit className="h-3.5 w-3.5 text-[#F5A623]" />
          </button>
        </div>
      </div>
    </Link>
  );
};
